<template>
    <div class="border rounded p-3">
        <template v-if="!handoff">
            <template v-if="requireSms">
                <label class="d-block font-weight-bold mb-1">{{ $t('eventRegistration.commitment.handoff.numberLabel') }}</label>
                <small class="text-muted d-block mb-2">{{ $t('eventRegistration.commitment.handoff.numberHint') }}</small>
                <base-input
                    :model-value="phone"
                    :name="name + ' :: handoffPhone'"
                    class="mb-0"
                    hide-label
                    type="tel"
                    :placeholder="$t('eventRegistration.commitment.mobileNumber')"
                    @update:model-value="emit('update:phone', $event)" />
                <small
                    v-if="phone && !phoneValid"
                    class="text-danger d-block mt-1">
                    {{ $t('eventRegistration.commitment.mobileInvalid') }}
                </small>
            </template>
            <p
                v-if="expired"
                class="text-muted mt-2 mb-0">
                {{ $t('eventRegistration.commitment.handoff.expired') }}
            </p>
            <div class="d-flex flex-wrap gap-2 mt-3">
                <base-button
                    type="primary"
                    :loading="creating"
                    :disabled="creating || (requireSms && !phoneValid)"
                    @click="create">
                    {{ $t('eventRegistration.commitment.handoff.' + (expired ? 'newLink' : 'createLink')) }}
                </base-button>
                <base-button
                    type="link"
                    class="shadow-none"
                    @click="emit('cancel')">
                    {{ $t('eventRegistration.commitment.handoff.signHere') }}
                </base-button>
            </div>
            <small
                v-if="error"
                class="text-danger d-block mt-1">
                {{ error }}
            </small>
        </template>
        <div
            v-else
            class="d-flex flex-column flex-sm-row gap-3">
            <img
                :src="handoff.qrCode"
                alt="QR-Code"
                width="200"
                height="200"
                class="flex-shrink-0 align-self-center align-self-sm-start">
            <div>
                <p class="mb-2">
                    {{ $t('eventRegistration.commitment.handoff.scanHint') }}
                </p>
                <p
                    v-if="requireSms"
                    class="mb-2">
                    {{ $t('eventRegistration.commitment.handoff.codeGoesTo', {number: phone}) }}
                </p>
                <small class="text-muted d-block mb-3">
                    {{ $t('eventRegistration.commitment.handoff.validFor', {remaining, time: $moment(handoff.expiresAt).format('HH:mm')}) }}
                </small>
                <p class="d-flex align-items-center gap-2 mb-3">
                    <font-awesome-icon
                        icon="circle-notch"
                        spin
                        class="text-primary" />
                    {{ $t('eventRegistration.commitment.handoff.waiting') }}
                </p>
                <div class="d-flex flex-wrap gap-2">
                    <base-button
                        type="primary"
                        outline
                        size="sm"
                        @click="copyLink">
                        {{ $t('eventRegistration.commitment.handoff.' + (copied ? 'linkCopied' : 'copyLink')) }}
                    </base-button>
                    <base-button
                        type="link"
                        size="sm"
                        class="shadow-none"
                        @click="cancel">
                        {{ $t('eventRegistration.commitment.handoff.cancel') }}
                    </base-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {commitmentErrorMessage} from "@/util/commitmentErrorMessage"

// Every open hand-off polls, and a large event can have many at once.
const POLL_INTERVAL_MS = 5000

const props = defineProps({
    eventId: {
        type: [String, Number],
        required: true
    },
    questionUuid: {
        type: String,
        required: true
    },
    requireSms: {
        type: Boolean,
        default: true
    },
    phone: {
        type: String,
        default: ''
    },
    phoneValid: {
        type: Boolean,
        default: false
    },
    name: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['update:phone', 'signed', 'cancel'])

const {t: $t} = useI18n()
const {$axios, $apiRoute, $moment} = useNuxtApp()

const handoff = ref(null)
const creating = ref(false)
const expired = ref(false)
const copied = ref(false)
const error = ref('')
let pollTimer = null
const {label: remaining} = useCountdown(computed(() => handoff.value?.expiresAt))

async function create() {
    creating.value = true
    error.value = ''
    try {
        handoff.value = (await $axios.post(
            $apiRoute('api.registration.veranstaltung.commitment.handoff.store', {veranstaltung: props.eventId}),
            {questionUuid: props.questionUuid, phone: props.requireSms ? props.phone : null}
        )).data
        expired.value = false
        copied.value = false
        schedulePoll()
    } catch (e) {
        error.value = commitmentErrorMessage($t, e, 'handoff.createFailed')
    }
    creating.value = false
}

function schedulePoll() {
    pollTimer = setTimeout(poll, POLL_INTERVAL_MS)
}

async function poll() {
    const current = handoff.value
    if (!current) return

    try {
        const {data} = await $axios.post(
            $apiRoute('api.registration.commitment.handoff.retrieve', {token: current.token}),
            {pollSecret: current.pollSecret}
        )
        if (handoff.value !== current) return
        if (data.signed) {
            handoff.value = null
            emit('signed', {signature: data.signature, verificationToken: data.verificationToken})
            return
        }
    } catch (e) {
        if (handoff.value !== current) return
        if (e.response?.status === 404) {
            handoff.value = null
            expired.value = true
            return
        }
        // a dropped request or a rate limit is not the end of the hand-off, the next poll tries again
    }
    schedulePoll()
}

function stopPolling() {
    clearTimeout(pollTimer)
    pollTimer = null
}

// Also drops the server entry, so the phone cannot sign into a hand-off nobody reads any more.
function cancel() {
    stopPolling()
    const current = handoff.value
    handoff.value = null
    if (!current) return

    $axios.delete(
        $apiRoute('api.registration.commitment.handoff.destroy', {token: current.token}),
        {data: {pollSecret: current.pollSecret}}
    ).catch(() => {
        // the entry expires on its own
    })
}

async function copyLink() {
    try {
        await navigator.clipboard.writeText(handoff.value.url)
        copied.value = true
    } catch {
        // no clipboard access; the QR code remains
    }
}

// cancel, not only stopPolling: a poll still in flight would otherwise find its hand-off current and go on
onBeforeUnmount(cancel)
</script>
